import { MarkHearthArch as HearthMark } from "../components/brand/Marks";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Loader2, XCircle, KeyRound } from "lucide-react";
import { resetLinkValid, completeReset } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NoIndex } from "../components/Seo";

export default function ResetPin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [valid, setValid] = useState(null);
  const [error, setError] = useState("");
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    resetLinkValid(token)
      .then(() => setValid(true))
      .catch((e) => {
        setValid(false);
        setError(e?.response?.data?.detail || "This reset link is invalid or has expired");
      });
  }, [token]);

  const submit = async () => {
    if (!/^\d{4,6}$/.test(pin)) return toast.error("PIN must be 4–6 digits");
    if (pin !== confirm) return toast.error("PINs don't match");
    setBusy(true);
    try {
      const { token: session } = await completeReset(token, pin);
      axios.defaults.headers.common["X-Vault-Token"] = session;
      toast.success("PIN updated — opening your vault");
      setTimeout(() => navigate("/vault", { replace: true }), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset the PIN");
      setBusy(false);
    }
    return undefined;
  };

  return (
    <div className="min-h-screen bg-background grid place-items-center px-6">
      <NoIndex title="Reset PIN" />
      <Toaster position="top-center" richColors />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="reset-pin-page" className="w-full max-w-sm bg-card border border-border rounded-2xl p-8 text-center">
        <div className="h-14 w-14 mx-auto rounded-2xl bg-primary text-primary-foreground grid place-items-center">
          <HearthMark className="h-7 w-7" />
        </div>
        {valid === null && <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mt-6" />}
        {valid === false && (
          <>
            <XCircle className="h-10 w-10 text-amber-500 mx-auto mt-6" />
            <h1 className="mt-4 font-serif text-2xl" data-testid="reset-invalid-title">Link expired</h1>
            <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          </>
        )}
        {valid && (
          <>
            <h1 className="mt-5 font-serif text-2xl" data-testid="reset-title">Choose a new owner PIN</h1>
            <p className="mt-1 text-sm text-muted-foreground">This link works once and only for the next few minutes.</p>
            <div className="mt-6 space-y-3 text-left">
              <Input data-testid="reset-new-pin-input" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="New PIN (4–6 digits)" className="bg-background" autoFocus />
              <Input data-testid="reset-confirm-pin-input" inputMode="numeric" maxLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} placeholder="Confirm PIN" className="bg-background" />
              <Button data-testid="reset-submit-btn" onClick={submit} disabled={busy} className="w-full gap-2 bg-primary hover:bg-primary/90 rounded-full">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Save PIN & enter vault
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
